import type { PauseInternetRuleStateStore } from '../types/store';
import { getRulesStore } from '../storage/stores';
import { nowImmediate } from '../lib/dateTime';

/**
 * Returns whether the Pause Internet rule is active at the given trusted time.
 * Operates on the normalized store shape so callers comparing two snapshots
 * (e.g. `onRulesUpdated`) can evaluate both with the same `nowMs`.
 */
export const isPauseInternetActiveAt = (
  pause: PauseInternetRuleStateStore | undefined,
  nowMs: number
): boolean => {
  const endsAtInSeconds = pause?.endsAtInSeconds ?? 0;
  if (endsAtInSeconds <= 0) {
    return false;
  }
  return endsAtInSeconds > Math.floor(nowMs / 1000);
};

export const PauseInternetRuleState = {
  NOT_PAUSED: 'notPaused',
  PAUSED_FOR: 'pausedFor',
} as const;

interface NotPaused {
  state: typeof PauseInternetRuleState.NOT_PAUSED;
}

interface PausedFor {
  state: typeof PauseInternetRuleState.PAUSED_FOR;
  endsAtInSeconds: number;
}

export type PauseInternetRuleState = NotPaused | PausedFor;

export const createPauseInternetRuleChecker = () => {
  const check = (): PauseInternetRuleState => {
    const pauseEndTimeInSeconds =
      getRulesStore().getPauseInternet().endsAtInSeconds;
    const secondsLeftInPause =
      pauseEndTimeInSeconds - Math.floor(nowImmediate() / 1000);

    if (secondsLeftInPause <= 0) {
      return { state: PauseInternetRuleState.NOT_PAUSED };
    }

    return {
      state: PauseInternetRuleState.PAUSED_FOR,
      endsAtInSeconds: pauseEndTimeInSeconds,
    };
  };

  return { check };
};

export const pauseInternetRuleChecker = createPauseInternetRuleChecker();
